<template>
  <div class="reply-box-v2" :class="{ 'is-private': isPrivateMode }">
    <div class="reply-box-v2__inner">
      <!-- Reply Box area -->
      <div class="reply-box-v2__editor-area">
        <!-- Top: mode tabs + expand -->
        <div class="reply-box-v2__top-row">
          <div class="reply-box-v2__left-row">
            <div class="reply-mode-tabs">
              <button
                class="reply-mode-btn"
                :class="{ 'reply-mode-btn--active': !isPrivateMode }"
                @click="setReplyMode('REPLY')"
              >
                <span class="material-symbols-outlined reply-mode-icon">
                  chat
                </span>
              </button>
              <button
                class="reply-mode-btn"
                :class="{ 'reply-mode-btn--note-active': isPrivateMode }"
                @click="setReplyMode('NOTE')"
              >
                <span class="material-symbols-outlined reply-mode-icon">
                  draft
                </span>
              </button>
            </div>
            <template v-if="shouldShowReplyToMessage">
              <div class="w-[1px] self-stretch bg-gray-200" />
              <reply-to-message
                :message="inReplyTo"
                :inbox="inbox"
                class="flex-1"
                @dismiss="resetReplyToMessage"
              />
            </template>
          </div>
          <button class="expand-btn" @click="$emit('popout')">
            <span class="material-symbols-outlined expand-icon">
              {{ popoutReplyBox ? 'close' : 'zoom_out_map' }}
            </span>
          </button>
        </div>

        <!-- Text input -->
        <woot-audio-recorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          @state-recorder-progress-changed="onStateProgressRecorderChanged"
          @state-recorder-changed="onStateRecorderChanged"
          @finish-record="onFinishRecorder"
        />
        <component
          :is="activeEditorComponent"
          v-else
          ref="activeEditor"
          v-model="message"
          class="input"
          :class="{ 'reply-box-v2__textarea': showRichContentEditor }"
          :placeholder="messagePlaceHolder"
          :disabled="!hasInboxPermission('Inbox reply')"
          :min-height="4"
          :signature="signatureToApply"
          :allow-signature="true"
          :attached-files="attachedFiles"
          :remove-attachment="removeAttachment"
          :inbox="inbox"
          v-bind="extraEditorProps"
          v-on="extraEditorListeners"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @paste="onPaste"
        >
          <template #toolbar-left>
            <left-toolbar
              :show-attach-button="showAttachButton"
              :allowed-attachments="allowedAttachments"
              :allowed-file-types="allowedFileTypes"
              :enable-multiple-file-upload="enableMultipleFileUpload"
              :enable-drag-and-drop="enableDragAndDrop"
              :show-docal-button="showDocalButton"
              :show-forms-button="showFormsButton"
              :conversation-id="conversationId"
              :inbox-id="inbox.id"
              :is-private-note="isPrivateMode"
              :message="message"
              :show-editor-toggle="showEditorToggle"
              :toggle-editor="toggleRichContentEditor"
              :show-ai-assistance="hasInboxPermission('Inbox ai assistance')"
              :show-rich-content-editor="showRichContentEditor"
              @input-file="file => fileUploadHandler(file, fileUploadOptions)"
              @input-filter="inputFilter"
              @signing-link="onDocalSigningLink"
              @form-link="onFormLink"
              @replace-text="replaceText"
              @selectWhatsappTemplate="openWhatsappTemplateModal"
            />
          </template>
          <template #toolbar-right>
            <right-toolbar
              :show-audio-recorder-button="showAudioRecorderButton"
              :is-recording-audio="isRecordingAudio"
              :is-send-button-disabled="isReplyButtonDisabled"
              :is-private-note="isPrivateMode"
              :inbox-id="inbox.id"
              @toggle-audio-recorder="toggleAudioRecorder"
              @send="onSend"
              @set-delay="setDelay"
            />
          </template>
        </component>
      </div>
    </div>
    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ReplyToMessage from 'dashboard/components/widgets/conversation/ReplyToMessage.vue';
import ResizableTextArea from './ResizableTextArea.vue';
import LeftToolbar from './LeftToolbar.vue';
import RightToolbar from './RightToolbar.vue';
import WootAudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/EditorV2.vue';

import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import alertMixin from 'shared/mixins/alertMixin';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';

import {
  trimContent,
  replaceVariablesInMessage,
  getUndefinedVariablesInMessage,
  getMessageVariables,
  debounce,
} from '@chatwoot/utils';
import { buildHotKeys } from 'shared/helpers/KeyboardHelpers';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import {
  appendSignature,
  removeSignature,
  replaceSignature,
  extractTextFromMarkdown,
} from 'dashboard/helper/editorHelper';

import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
} from 'shared/constants/messages';
import { isFileExtAllowed } from 'dashboard/helper/commons';

export default {
  name: 'ReplyBoxV2',
  components: {
    LeftToolbar,
    RightToolbar,
    ReplyToMessage,
    ResizableTextArea,
    WootAudioRecorder,
    WootMessageEditor,
  },
  mixins: [
    alertMixin,
    fileUploadMixin,
    hasPermissionMixin,
    inboxMixin,
    uiSettingsMixin,
  ],
  inject: [
    'replyType',
    'isPrivateMode',
    'setReplyMode',
    'showWhatsAppTemplatesModal',
    'openWhatsappTemplateModal',
    'hideWhatsappTemplatesModal',
  ],
  props: {
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
    initialMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      message: this.initialMessage,
      isFocused: false,
      inReplyTo: {},
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isRecordingAudio: false,
      isRecorderAudioStopped: false,
      attachedFiles: [],
      updateEditorSelectionWith: '',
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showDocalModal: false,
      undefinedVariableMessage: '',
      delayHours: null,
      doAutoSaveDraft: () => {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      getMessageSignature: 'getMessageSignature',
      globalConfig: 'globalConfig/get',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    inboxId() {
      return this.currentChat?.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    account() {
      return this.getAccount(this.accountId);
    },
    conversationId() {
      return this.currentChat?.id;
    },
    isReplyButtonDisabled() {
      if (!this.hasInboxPermission('Inbox reply')) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    hasAttachments() {
      return this.attachedFiles.length > 0;
    },
    hasRecordedAudio() {
      return (
        this.$refs.audioRecorderInput &&
        this.$refs.audioRecorderInput.hasAudio()
      );
    },
    isMessageEmpty() {
      if (!this.message) return true;
      return !this.message.trim().replace(/\n/g, '').length;
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    messageVariables() {
      return getMessageVariables({ conversation: this.currentChat });
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivateMode &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.MESSAGE_REPLY_TO
        )
      );
    },
    showAudioRecorder() {
      return (
        !this.isPrivateMode &&
        this.showFileUpload &&
        !this.isALineChannel &&
        !this.isAFacebook2Inbox &&
        !this.isA99digitalSmsInbox &&
        !this.isAnEmailChannel
      );
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isAPIInbox) {
        return AUDIO_FORMATS.OGG;
      }
      return AUDIO_FORMATS.WAV;
    },
    fileUploadOptions() {
      return {
        isMailboxFileUpload: false,
        isWhatsAppFileUpload: this.isAWhatsAppChannel,
      };
    },
    showRichContentEditor() {
      if (this.isPrivateMode || this.isAWebWidgetInbox) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      return false;
    },
    messagePlaceHolder() {
      return this.isPrivateMode
        ? this.$t('CONVERSATION.FOOTER.TYPING_A_NOTE')
        : this.$t('CONVERSATION.FOOTER.TYPING_A_MESSAGE');
    },
    messageSignature() {
      const signature = this.getMessageSignature(this.inboxId)?.body || '';
      if (this.attachmentSignature) {
        return (
          signature +
          `\n ![This is signature](${this.attachmentSignature} "This is signature")`
        );
      }
      return signature;
    },
    attachmentSignature() {
      return this.getMessageSignature(this.inboxId)?.attachment_url;
    },
    signatureToApply() {
      return this.showRichContentEditor
        ? this.messageSignature
        : extractTextFromMarkdown(this.messageSignature);
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    showFileUpload() {
      if (!this.hasInboxPermission('Inbox attach files')) return false;
      if (this.isA99digitalSmsInbox) return false;
      return (
        this.isAWebWidgetInbox ||
        this.isALineChannel ||
        this.isAFacebookInbox ||
        this.isAFacebook2Inbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel
      );
    },
    sendWithSignature() {
      return !!this.messageSignature;
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivateMode && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.signatureToApply;
    },
    showAttachButton() {
      return this.showFileUpload || this.isPrivateMode;
    },
    allowedAttachments() {
      return this.isALineChannel
        ? 'image/*,video/*'
        : this.isAFacebook2Inbox
        ? 'image/*'
        : undefined;
    },
    allowedFileTypes() {
      if (this.isATwilioWhatsAppChannel) {
        return ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP;
      }
      return ALLOWED_FILE_TYPES;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel
      );
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    showAudioRecorderButton() {
      // Disable audio recorder for safari browser as recording is not supported
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
        navigator.userAgent
      );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) &&
        this.showAudioRecorder &&
        !isSafari
      );
    },
    showDocalButton() {
      return this.account?.additional_attributes?.document_signature_enabled;
    },
    showFormsButton() {
      return !!this.account?.forms_enabled;
    },
    showEditorToggle() {
      return (
        this.isAPIInbox && !this.isPrivateMode && !this.isSomeOfOnlineWaInbox
      );
    },
    activeEditorComponent() {
      return this.showRichContentEditor
        ? 'woot-message-editor'
        : 'resizable-text-area';
    },
    extraEditorProps() {
      if (this.showRichContentEditor) {
        return {
          editorId: this.editorStateId,
          isPrivate: this.isPrivateMode,
          updateSelectionWith: this.updateEditorSelectionWith,
          enableVariables: true,
          variables: this.messageVariables,
        };
      }
      return { sendWithSignature: this.sendWithSignature };
    },
    extraEditorListeners() {
      if (this.showRichContentEditor) {
        return {
          'toggle-user-mention': this.toggleUserMention,
          'toggle-canned-menu': this.toggleCannedMenu,
          'toggle-variables-menu': this.toggleVariablesMenu,
          'clear-selection': this.clearEditorSelection,
        };
      }
      return {};
    },
  },
  watch: {
    initialMessage(newVal) {
      this.message = newVal;
    },
    message(newVal) {
      this.$emit('update:initialMessage', newVal);
      this.doAutoSaveDraft();
    },
    currentChat(conversation, oldConversation) {
      if (conversation.id === oldConversation.id) return;
      this.fetchAndSetReplyTo();
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
      }
    },
  },
  mounted() {
    this.getFromDraft();
    this.fetchAndSetReplyTo();
    this.$el.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );
    bus.$on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    bus.$on(BUS_EVENTS.TOGGLE_FORWARD_TO_MESSAGE, this.onToggleNote);
    bus.$on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
  },
  destroyed() {
    this.$el.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    bus.$off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    bus.$off(BUS_EVENTS.TOGGLE_FORWARD_TO_MESSAGE, this.onToggleNote);
    bus.$off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
  },
  methods: {
    toggleDocal() {
      this.showDocalModal = !this.showDocalModal;
    },
    onDocalSigningLink(url) {
      this.showDocalModal = false;
      this.appendLinkToMessage(url);
    },
    onFormLink(url) {
      this.appendLinkToMessage(url);
    },
    appendLinkToMessage(url) {
      const currentMsg = this.message || '';
      const separator = currentMsg.trim() ? ' ' : '';
      this.message = currentMsg + separator + url;
    },
    onSend() {
      this.onSendReply();
    },
    replaceText(message) {
      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });
      setTimeout(() => {
        this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );
      const mode = LocalStorage.getFromJsonStore(replyStorageKey, 'mode');
      if (mode && mode !== this.replyType) {
        this.setReplyMode(mode);
      }
      this.inReplyTo = this.currentChat?.messages?.find(
        message => message.id === replyToMessageId
      );
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onToggleNote() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_FORWARD_TO;
      const mode = LocalStorage.getFromJsonStore(replyStorageKey, 'mode');
      if (mode) {
        this.setReplyMode(mode);
      }
    },
    onStateProgressRecorderChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onStateRecorderChanged(state) {
      this.recordingAudioState = state;
      if (state && 'notallowederror'.includes(state)) {
        this.toggleAudioRecorder();
      }
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.clearMessage();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (this.isRecordingAudio) {
        if (!this.isRecorderAudioStopped) {
          this.isRecorderAudioStopped = true;
          this.$refs.audioRecorderInput.stopAudioRecording();
        } else if (this.isRecorderAudioStopped) {
          this.$refs.audioRecorderInput.playPause();
        }
      }
    },
    clearMessage() {
      this.message = '';
      if (this.sendWithSignature && !this.isPrivateMode) {
        // if signature is enabled, append it to the message
        this.message = appendSignature(this.message, this.signatureToApply);
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
    },
    onFinishRecorder(file) {
      return file && this.fileUploadHandler(file, this.fileUploadOptions);
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      if (!conversationId) return;
      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate: this.isPrivateMode,
      });
    },
    toggleUserMention(value) {
      this.showUserMentions = value;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivateMode,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    triggerOpenUploadFile() {
      this.$refs.uploadAttachment.$el
        .querySelector('input[type="file"]')
        .click();
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!isFileExtAllowed(newFile.name, this.fileKindsAcceptedToUpload)) {
          return prevent();
        }
      }
      return false;
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });

      const plainTextSignature = extractTextFromMarkdown(this.messageSignature);

      if (!this.showRichContentEditor && this.messageSignature) {
        // remove the old signature -> extract text from markdown -> attach new signature
        let message = removeSignature(this.message, this.messageSignature);
        message = extractTextFromMarkdown(message);
        message = appendSignature(message, plainTextSignature);

        this.message = message;
      } else {
        this.message = replaceSignature(
          this.message,
          plainTextSignature,
          this.messageSignature
        );
      }
    },
    // Draft management
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivateMode) return message;
      return this.sendWithSignature
        ? appendSignature(message, this.signatureToApply)
        : removeSignature(message, this.signatureToApply);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    // Sending
    confirmOnSendReply() {
      if (
        !this.hasAttachments &&
        !this.hasRecordedAudio &&
        this.isMessageEmpty
      ) {
        return;
      }
      const isOnWhatsApp =
        this.isATwilioWhatsAppChannel ||
        this.isAWhatsAppCloudChannel ||
        this.is360DialogWhatsAppChannel;
      if (isOnWhatsApp && !this.isPrivateMode) {
        this.sendMessageAsMultipleMessages(this.message);
      } else {
        const messagePayload = this.getMessagePayload(this.message);
        this.sendMessage(messagePayload);
      }
      this.clearMessage();
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        bus.$emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMessagePayloadForWhatsapp(message);
      messages.forEach(messagePayload => {
        this.sendMessage(messagePayload);
      });
    },
    sendMessageAnalyticsData(isPrivate) {
      return isPrivate
        ? this.$track(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : this.$track(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    getMessagePayload(message) {
      const messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivateMode,
        sender: this.sender,
        delay_hours: this.delayHours,
      };
      if (this.inReplyTo?.id) {
        messagePayload.contentAttributes = {
          in_reply_to: this.inReplyTo.id,
        };
      }
      if (this.isAPIInbox && this.messageSignature) {
        messagePayload.contentAttributes = {
          ...messagePayload.contentAttributes,
          message_signature: this.messageSignature,
        };
      }
      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = messagePayload.files || [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }
      return messagePayload;
    },
    getMessagePayloadForWhatsapp(message) {
      const multipleMessagePayload = [];
      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          multipleMessagePayload.push({
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          });
          caption = '';
        });
      } else {
        multipleMessagePayload.push({
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        });
      }
      return multipleMessagePayload;
    },
    // Attachments
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    // Paste handler
    onPaste(e) {
      if (!this.hasInboxPermission('Inbox attach files')) return;
      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.activeEditor.$el.blur();
      }
      if (!data.length || !data[0]) return;
      data.forEach(file => {
        const { name, type, size } = file;
        this.fileUploadHandler(
          { name, type, size, file },
          this.fileUploadOptions
        );
      });
    },
    // Keyboard shortcuts
    handleKeyEvents(e) {
      const keyCode = buildHotKeys(e);
      if (keyCode === 'enter' && this.isAValidEvent('enter')) {
        this.onSendReply();
        e.preventDefault();
      } else if (
        ['meta+enter', 'ctrl+enter'].includes(keyCode) &&
        this.isAValidEvent('cmd_enter')
      ) {
        this.onSendReply();
      }
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        isEditorHotKeyEnabled(this.uiSettings, selectedKey)
      );
    },
    // New conversation modal drag/drop fix
    onNewConversationModalActive(isActive) {
      this.newConversationModalActive = isActive;
    },
    setDelay(delayHours) {
      this.delayHours = delayHours;
      this.onSendReply();
      this.delayHours = null;
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.reply-box-v2 {
  background: #fafafa;
  border-top: 1px solid #e5e5e5;
  width: 100%;

  &.is-private {
    background: #fefce8;
    border-top: 1px solid #fde68a;
  }
}

.reply-box-v2__inner {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 12px;
  height: 100%;
}

/* Editor area */
.reply-box-v2__editor-area {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
}

.reply-box-v2__top-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.reply-box-v2__left-row {
  display: flex;
  align-items: start;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

/* Mode tabs */
.reply-mode-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #f3f4f6;
  border-radius: 6px;
}

.reply-mode-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 5px;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  color: #9ca3af;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }

  &--active {
    background: #2abb7f;
    color: #ffffff;

    &:hover {
      background: #23a870;
      color: #ffffff;
    }
  }

  &--note-active {
    background: #f59e0b;
    color: #ffffff;

    &:hover {
      background: #f59e0b;
      color: #ffffff;
    }
  }
}

.reply-mode-icon {
  font-size: 16px;
  line-height: 1;
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 16;
}

/* Expand button */
.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: #9ca3af;
  flex-shrink: 0;

  &:hover {
    color: #6b7280;
  }
}

.expand-icon {
  font-size: 16px;
  line-height: 1;
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 16;
}

/* Textarea */
.reply-box-v2__textarea {
  width: 100%;
  min-height: 44px;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #374151;
  placeholder-color: #9ca3af;

  &::placeholder {
    color: #9ca3af;
  }
}

/* Actions row */
.reply-box-v2__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.reply-box-v2__actions-start {
  display: flex;
  align-items: center;
  gap: 10px;
}

.reply-box-v2__actions-end {
  display: flex;
  align-items: center;
  gap: 8px;
}

.end-icon-group {
  display: flex;
  align-items: center;
}

/* Action icon button (toolbar icons) */
.action-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  transition: background 0.15s ease, color 0.15s ease;

  .material-symbols-outlined {
    font-size: 20px;
    line-height: 1;
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20;
  }

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  /* AI button */
  &--ai {
    width: 32px;
    height: 32px;
    background: #f0fdf4;
    border: 1px solid #e3ef26;
    border-radius: 4px;
    box-shadow: 0px 1px 8px 0px rgba(221, 221, 221, 0.25);
    color: #15803d;

    &:hover {
      background: #dcfce7;
      color: #166534;
    }
  }
}
</style>
